import { css } from '@emotion/react';
import elevate from 'storybookConfig/mixins/elevate';
import theme from 'storybookConfig/stories/theme';
import type { ThemeColor } from 'storybookConfig/stories/theme';

export interface LiftOptions {
  /** The edge the surface takes while it is lifted. */
  edge?: ThemeColor;
  /** Lift under the pointer and on keyboard focus. Off for a surface that isn't clickable. */
  interactive?: boolean;
  /** Hold it lifted, for a selected surface. */
  selected?: boolean;
  /** A selector inside the surface that marks it selected, e.g. a ticked checkbox. */
  selectedWhen?: string;
}

/**
 * The one hover and selection treatment of a clickable surface (search results, partner cards,
 * decision buttons): an edge in `edge` and the first elevation, eased. Keyboard focus, on the
 * surface or inside it, lifts it as the pointer does.
 */
const lift = ({
  edge = 'accent',
  interactive = true,
  selected = false,
  selectedWhen,
}: LiftOptions = {}) => {
  const lifted = css`
    border-color: ${theme.color[edge]};
    ${elevate('1')};
  `;

  return css`
    transition:
      border-color ${theme.motion.hover} ${theme.motion.easeOut},
      box-shadow ${theme.motion.hover} ${theme.motion.easeOut};

    ${interactive &&
    css`
      &:hover,
      &:focus-visible,
      &:has(:focus-visible) {
        ${lifted};
      }
    `}

    ${selectedWhen &&
    css`
      &:has(${selectedWhen}) {
        ${lifted};
      }
    `}

    ${selected && lifted};
  `;
};

export default lift;
